import React, { useState, useContext, useEffect } from "react";
import classnames from "classnames";
import { Text, MobileContext, RadioButtons } from "components/common";
import { useMutation } from "@apollo/react-hooks";
import {
  FREEZE_USER_CARD_BY_USER,
  LOCKED_STATUS_QUERY,
  UNFREEZE_USER_CARD_BY_USER,
} from "utils/mutations";
import {
  CARD_STATUS,
  ERROR_STATUS,
  CARD_LOCKED_REASONS,
} from "utils/constants";
import { track } from "../../../../../../../utils/analytics";

const FreezeCard = ({
  user,
  roles,
  resetSuccess = () => {},
  SettingsButton,
}) => {
  const meta = JSON.parse(user?.card?.metadata || "{}");
  const locked_reasons = meta.locked_reasons || {};
  const { isMobile } = useContext(MobileContext);
  const [initialStatus, setInitialStatus] = useState(user.card?.status || null);
  const [cardStatus, setCardStatus] = useState(initialStatus);
  const { missed_payment } = locked_reasons || {};
  const hasLockedReasons = !!Object.keys(locked_reasons || {}).length;
  const preventUserUnlock =
    hasLockedReasons &&
    !!Object.keys(locked_reasons).find(
      (r) => !CARD_LOCKED_REASONS.USER_INITIATED.includes(r)
    );
  const [errorMessage, setErrorMessage] = useState("");
  const [inProgress, setInProgress] = useState(false);

  const [freezeUserCardByUser] = useMutation(FREEZE_USER_CARD_BY_USER, {
    update: (cache) => {
      cache.writeData({
        data: {
          user: {
            __typename: "User",
            card: {
              __typename: "UserCard",
              status: cardStatus,
            },
          },
        },
      });
      setInitialStatus(cardStatus);
    },
    refetchQueries: [{ query: LOCKED_STATUS_QUERY }],
  });

  const [unfreezeUserCardByUser] = useMutation(UNFREEZE_USER_CARD_BY_USER, {
    update: (cache) => {
      cache.writeData({
        data: {
          user: {
            __typename: "User",
            card: {
              __typename: "UserCard",
              status: cardStatus,
            },
          },
        },
      });
      setInitialStatus(cardStatus);
    },
    refetchQueries: [{ query: LOCKED_STATUS_QUERY }],
  });

  const disableOption = () => {
    //  Admins are allowed always, don't disable
    if (roles.admin) {
      return false;
    }

    if (initialStatus) {
      // disable UI
      if (
        (initialStatus === CARD_STATUS.LOCKED && preventUserUnlock) ||
        [
          CARD_STATUS.INACTIVE,
          CARD_STATUS.PENDING,
          CARD_STATUS.TERMINATED,
          CARD_STATUS.UNKNOWN,
        ].includes(initialStatus)
      ) {
        return true;
      }
    }

    // No need to disable
    return false;
  };
  const [optionDisabled, setOptionDisabled] = useState(disableOption());

  const handleCardStatusChange = (element) => {
    setCardStatus(element.id);
    resetSuccess();
  };

  const handleCardAction = (e) => {
    if (!cardStatus) {
      setErrorMessage("Can't update card status right now");
      return false;
    }
    setInProgress(true);
    setErrorMessage("");
    track("DASHBOARD_SETTINGS_MANAGE_CARD_STATUS_CHANGE_SUBMITTED");

    if (cardStatus === CARD_STATUS.LOCKED) {
      return freezeUserCardByUser()
        .then((res) => {
          if (res.data.freezeUserCardByUser.status === ERROR_STATUS) {
            setInProgress(false);
            setErrorMessage("Cannot complete");
            return { success: false };
          }
          setInProgress(false);
          // setIsUpdated(true);
          track(
            `DASHBOARD_SETTINGS_MANAGE_CARD_STATUS_SUCCESS_${cardStatus.toUpperCase()}ED`
          );
          return { success: true };
        })
        .catch((e) => {
          setInProgress(false);
          setErrorMessage("Cannot complete");
          return { success: false };
        });
    }

    //  Allow the user to unfreeze their card if there are NOT any locked reasons OR if the user is Admin
    if (
      cardStatus === CARD_STATUS.ACTIVE &&
      (!preventUserUnlock || roles.admin)
    ) {
      return unfreezeUserCardByUser()
        .then((res) => {
          if (res.data.unfreezeUserCardByUser.status === ERROR_STATUS) {
            setInProgress(false);
            setErrorMessage("Cannot complete");
            return { success: false };
          }
          setInProgress(false);
          // setIsUpdated(true);
          track(
            `DASHBOARD_SETTINGS_MANAGE_CARD_STATUS_SUCCESS_${cardStatus.toUpperCase()}ED`
          );
          return { success: true };
        })
        .catch((e) => {
          setInProgress(false);
          setErrorMessage("Cannot complete");
          return { success: false };
        });
    }

    setInProgress(false);
    setErrorMessage("An error has occured");
    return { success: false };
  };

  const displayLockMessage = () => {
    //  If there are ANY locked_reasons AND the user is NOT an admin AND the card is locked
    //  display the meaningful lock message
    if (
      preventUserUnlock &&
      !roles.admin &&
      initialStatus === CARD_STATUS.LOCKED
    ) {
      return missed_payment ? (
        <>
          Please pay your balance in full to UNLOCK your card. Once payments
          settle (within 3 business days), your card will be unlocked.
        </>
      ) : (
        "Please contact customer support to unlock"
      );
    }
  };
  const [lockMessage, setLockMessage] = useState(displayLockMessage() || "");

  useEffect(() => {
    setOptionDisabled(disableOption());
    setLockMessage(displayLockMessage());
  }, [initialStatus]);

  return (
    <div className="flex-1">
      <div className="">
        <div
          className={classnames("mb-3", {
            "mt-4 pl-4": isMobile,
          })}
          style={{ maxWidth: "400px" }}
        >
          <RadioButtons
            elements={[
              {
                id: CARD_STATUS.ACTIVE,
                displayName: "Active",
                selected: cardStatus === CARD_STATUS.ACTIVE,
              },
              {
                id: CARD_STATUS.LOCKED,
                displayName: "Lock Card",
                selected: cardStatus === CARD_STATUS.LOCKED,
              },
            ]}
            component={(element) => {
              return element.element.displayName;
            }}
            didSelect={handleCardStatusChange}
            className="mr-2"
          />
          <div>{lockMessage}</div>
        </div>
        <Text className={errorMessage ? "" : "hidden"}>{errorMessage}</Text>
        <SettingsButton
          className={inProgress ? "disabled" : ""}
          disabled={optionDisabled}
          onClick={handleCardAction}
        >
          Save changes
        </SettingsButton>
      </div>
    </div>
  );
};

export default FreezeCard;
